import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

export const createPatientData = createAsyncThunk(
  "/patient/patientRegister",
  async ({ formDataToSend, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/patient/patientRegister",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            token,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getAllPatient = createAsyncThunk(
  "patient/getAllPatients",
  async (patientData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/patient/getAllPatients",
        patientData,
        {
          headers: {
            "Content-Type": "application/json",
            token: patientData.token,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updatePatientSlice = createAsyncThunk(
  "patient/updatePatient",
  async ({ formDataToSend, token }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        "/patient/updatePatient",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            token,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getPatientIdSlice = createAsyncThunk(
  "patient/getPatientById",
  async (patientData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/patient/getPatientById",
        patientData,
        {
          headers: {
            "Content-Type": "application/json",
            token: patientData.token,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deletePatientSlice = createAsyncThunk(
  "patient/deletedPatients",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.put("/patient/deletedPatients", data, {
        headers: {
          "Content-Type": "application/json",
          token: data.token,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getPatientRegistrationStats = createAsyncThunk(
  "patient/getPatientRegistrationStats",
  async (patientData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/patient/getPatientRegistrationStats",
        patientData,
        {
          headers: {
            "Content-Type": "application/json",
            token: patientData.token,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addOrUpdateReport = createAsyncThunk(
  "patient/addOrUpdateReport",
  async ({ formDataToSend, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/patient/addOrUpdateReport",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            token: token,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteReportSlice = createAsyncThunk(
  "patient/deleteReport",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.put("/patient/deleteReport", data, {
        headers: {
          "Content-Type": "application/json",
          token: data.token,
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addOrUpdateVitalReport = createAsyncThunk(
  "patient/addOrUpdateVitalReport",
  async ({ formDataToSend, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/patient/addOrUpdateVitalReport",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            token,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const PatientDetails = createSlice({
  name: "patientData",
  initialState: {
    loading: false,
    isLoading: false,
    successMessage: null,
    patientErrorMessage: null,
    errorMessage: null,
    updateSuccessMessage: null,
    updateErrorMessage: null,
    deleteSuccessMessage: null,
    deleteErrorMessage: null,
    patients: [],
    patientData: null,
    patientDataForPatientId: null,
    patientDataRequestId: null,
    currentPage: 1,
    totalPages: null,
    hasMore: true,
    totalCount: 0,
    patientStats: {
      yesterdayPatientCount: 0,
      todayPatientCount: 0,
      registrationPercentageChange: "0%",
    },
  },
  reducers: {
    resetMessage: (state) => {
      state.successMessage = null;
      state.errorMessage = null;
      state.patientErrorMessage = null;
    },
    resetUpdateMessages: (state) => {
      state.updateSuccessMessage = null;
      state.updateErrorMessage = null;
    },
    resetDeleteMessages: (state) => {
      state.deleteSuccessMessage = null;
      state.deleteErrorMessage = null;
    },
    resetPatientData: (state) => {
      state.patientStats = {
        yesterdayPatientCount: 0,
        todayPatientCount: 0,
        registrationPercentageChange: "0%",
      };
    },
    resetCurrentPatientProfile: (state) => {
      state.patientData = null;
      state.patientDataForPatientId = null;
      state.patientDataRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPatientData.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
        state.successMessage = null;
        state.patientErrorMessage = null;
        state.updateErrorMessage = null;
      })
      .addCase(createPatientData.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        if (action.payload.success === true) {
          state.successMessage = action.payload.result.message;
        } else {
          state.patientErrorMessage = action.payload.result.message;
        }
      })
      .addCase(createPatientData.rejected, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        state.patientErrorMessage = action.payload;
      })
      .addCase(getAllPatient.pending, (state, action) => {
        state.loading = true;
        state.isLoading = true;
      })
      .addCase(getAllPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        if (!action.payload.success) {
          state.patients = [];
          state.totalCount = 0;
          state.totalPages = 1;
          state.searchMessage =
            action.payload.result?.message || "No data found";
          return;
        }
        const result = action.payload.result;
        state.patients = result.list || [];
        state.currentPage = Number(result.currentPage) || 1;
        state.totalPages = Number(result.totalPages) || 1;
        state.totalCount = Number(result.totalRecords) || 0;
        state.hasMore = result.hasMore ?? false;
        state.searchMessage = null;
      })
      .addCase(getAllPatient.rejected, (state, action) => {
        state.loading = false;
        state.isLoading = false;
      })
      .addCase(updatePatientSlice.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
        state.updateSuccessMessage = null;
        state.updateErrorMessage = null;
      })
      .addCase(updatePatientSlice.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        if (action.payload.success === false) {
          state.updateErrorMessage = action.payload.result.message;
        } else {
          state.updateSuccessMessage = action.payload.result.message;
        }
      })
      .addCase(updatePatientSlice.rejected, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        state.updateErrorMessage = action.payload.result.message;
      })
      .addCase(getPatientIdSlice.pending, (state, action) => {
        state.patientData = null;
        state.patientDataForPatientId = String(action.meta.arg?.patientId || "");
        state.patientDataRequestId = action.meta.requestId;
        state.loading = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPatientIdSlice.fulfilled, (state, action) => {
        if (state.patientDataRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.isLoading = false;
        const requestedPatientId = String(action.meta.arg?.patientId || "");
        const payloadPatientId = String(
          action.payload?.result?.list?.patientId ||
            action.payload?.result?.list?._id ||
            "",
        );
        state.patientData =
          payloadPatientId && requestedPatientId && payloadPatientId !== requestedPatientId
            ? null
            : action.payload.result.list;
      })
      .addCase(getPatientIdSlice.rejected, (state, action) => {
        if (state.patientDataRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.isLoading = false;
        state.patientData = null;
        state.error = action?.payload?.result?.message;
      })
      .addCase(deletePatientSlice.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
        state.deleteSuccessMessage = null;
        state.deleteErrorMessage = null;
      })
      .addCase(deletePatientSlice.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        if (action.payload.success === false) {
          state.deleteErrorMessage = action.payload.result.message;
        } else {
          state.deleteSuccessMessage = action.payload.result.message;
        }
      })
      .addCase(deletePatientSlice.rejected, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        state.deleteErrorMessage = action.payload.result.message;
      })
      .addCase(getPatientRegistrationStats.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
      })
      .addCase(getPatientRegistrationStats.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        if (action.payload.success === false) {
          state.errorMessage = action.payload.result.message;
        } else {
          state.patientStats = action.payload.result.list;
        }
      })
      .addCase(getPatientRegistrationStats.rejected, (state, action) => {
        state.loading = false;
        state.isLoading = false;
        state.errorMessage = action.payload;
      })
      .addCase(addOrUpdateReport.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
      })
      .addCase(addOrUpdateReport.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
      })
      .addCase(addOrUpdateReport.rejected, (state) => {
        state.loading = false;
        state.isLoading = false;
      })
      .addCase(addOrUpdateVitalReport.pending, (state) => {
        state.loading = true;
        state.isLoading = true;
      })
      .addCase(addOrUpdateVitalReport.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoading = false;
      })
      .addCase(addOrUpdateVitalReport.rejected, (state) => {
        state.loading = false;
        state.isLoading = false;
      });
  },
});
export const {
  resetMessage,
  resetUpdateMessages,
  resetDeleteMessages,
  resetPatientData,
  resetCurrentPatientProfile,
} = PatientDetails.actions;
export default PatientDetails.reducer;
